import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  BuildingStorefrontIcon,
  CalendarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';

const CONTACT_METHODS = [
  { id: 'call', label: 'Phone Call', icon: PhoneIcon, color: '#10b981' },
  { id: 'email', label: 'Email', icon: EnvelopeIcon, color: '#3b82f6' },
  { id: 'text', label: 'Text/SMS', icon: ChatBubbleLeftRightIcon, color: '#8b5cf6' },
  { id: 'meeting', label: 'In-Person', icon: UserGroupIcon, color: '#f59e0b' },
  { id: 'walk_in', label: 'Walk-In Visit', icon: BuildingStorefrontIcon, color: '#ec4899' },
  { id: 'dm', label: 'Social DM', icon: ChatBubbleLeftRightIcon, color: '#06b6d4' }
];

const OUTCOMES = [
  { id: 'interested', label: 'Interested - Follow Up', color: '#10b981' },
  { id: 'callback', label: 'Requested Callback', color: '#3b82f6' },
  { id: 'meeting_scheduled', label: 'Meeting Scheduled', color: '#8b5cf6' },
  { id: 'not_interested', label: 'Not Interested', color: '#ef4444' },
  { id: 'no_answer', label: 'No Answer', color: '#6b7280' },
  { id: 'wrong_number', label: 'Wrong Number', color: '#f59e0b' },
  { id: 'voicemail', label: 'Left Voicemail', color: '#06b6d4' },
  { id: 'other', label: 'Other', color: '#9ca3af' }
];

const ContactModal = ({ lead, onClose, onSuccess, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    contacted_by: currentUser?.id || '',
    contact_method: 'call',
    outcome: '',
    notes: '',
    follow_up_date: '',
    new_status: 'contacted'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/sales/users');
      setUsers(res.data);
      // Default to current user if in list
      if (currentUser && !form.contacted_by) {
        setForm(f => ({ ...f, contacted_by: currentUser.id }));
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmit = async () => {
    if (!form.contacted_by) {
      toast.warning('Please select who made contact');
      return;
    }
    if (!form.contact_method) {
      toast.warning('Please select contact method');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/sales/leads/${lead.id}/contact`, form);
      toast.success('Contact recorded successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to record contact');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      padding: 20
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        width: '100%',
        maxWidth: 600,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: 24,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: 'white',
          zIndex: 1
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PhoneIcon style={{ width: 24, height: 24, color: 'white' }} />
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Record Contact</h2>
              <p style={{ fontSize: 13, color: '#718096', margin: 0 }}>{lead.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
          >
            <XMarkIcon style={{ width: 24, height: 24 }} />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {/* Contact By */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#374151' }}>
              Who Made Contact? *
            </label>
            <select
              value={form.contacted_by}
              onChange={(e) => setForm({ ...form, contacted_by: e.target.value })}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 15,
                background: 'white'
              }}
            >
              <option value="">Select team member...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </option>
              ))}
            </select>
          </div>

          {/* Contact Method */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#374151' }}>
              Contact Method *
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {CONTACT_METHODS.map(method => {
                const Icon = method.icon;
                const isSelected = form.contact_method === method.id;
                return (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setForm({ ...form, contact_method: method.id })}
                    style={{
                      padding: '14px 12px',
                      background: isSelected ? `${method.color}15` : '#f9fafb',
                      border: `2px solid ${isSelected ? method.color : '#e2e8f0'}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      transition: 'all 0.2s'
                    }}
                  >
                    <Icon style={{ width: 22, height: 22, color: isSelected ? method.color : '#6b7280' }} />
                    <span style={{ 
                      fontSize: 12, 
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? method.color : '#4b5563'
                    }}>
                      {method.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Outcome */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#374151' }}>
              Outcome
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {OUTCOMES.map(outcome => {
                const isSelected = form.outcome === outcome.id;
                return (
                  <button
                    key={outcome.id}
                    type="button"
                    onClick={() => setForm({ ...form, outcome: outcome.id })}
                    style={{
                      padding: '10px 14px',
                      background: isSelected ? `${outcome.color}15` : 'white',
                      border: `2px solid ${isSelected ? outcome.color : '#e2e8f0'}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      transition: 'all 0.2s'
                    }}
                  >
                    {isSelected && <CheckCircleIcon style={{ width: 16, height: 16, color: outcome.color }} />}
                    <span style={{ 
                      fontSize: 13, 
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? outcome.color : '#4b5563'
                    }}>
                      {outcome.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#374151' }}>
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Add any notes about the conversation..."
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 14,
                minHeight: 100,
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Follow-up Date */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#374151' }}>
              <CalendarIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
              Schedule Follow-up
            </label>
            <input
              type="date"
              value={form.follow_up_date}
              onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 14
              }}
            />
          </div>

          {/* Update Status */}
          <div style={{ 
            marginBottom: 24, 
            padding: 16, 
            background: '#f9fafb', 
            borderRadius: 10,
            border: '1px solid #e2e8f0'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.new_status === 'contacted'}
                onChange={(e) => setForm({ 
                  ...form, 
                  new_status: e.target.checked ? 'contacted' : lead.lead_status 
                })}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                Update lead status to "Contacted"
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: 20,
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          background: '#f9fafb'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              background: '#e2e8f0',
              color: '#374151',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '12px 32px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {loading ? 'Saving...' : 'Record Contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
